/* @jsx h */
import { defineComponent, h, ref, onMounted, computed } from "vue";
import { ProductRequirement, Product } from "@lemmonhq/types";
import {
  SelectableListComboBox,
  SelectableItem,
} from "@app/vue/components/index";
import { api } from "@app/data/index";
import { useAuth } from "@app/vue/providers/Auth";

export type ProductRequirementEditorProps = {
  value: ProductRequirement;
  onChange: (requirement: ProductRequirement) => void;
};

export const ProductRequirementEditor =
  defineComponent<ProductRequirementEditorProps>({
    name: "ProductRequirementEditor",
    props: ["value", "onChange"],
    setup(props) {
      const auth = useAuth();
      const products = ref<Product[]>([]);
      const loading = ref(false);

      const loadProducts = async () => {
        if (loading.value || products.value.length > 0) return;
        try {
          loading.value = true;
          products.value = await api.getProducts();
        } catch (error) {
          console.error("Error loading products:", error);
        } finally {
          loading.value = false;
        }
      };

      onMounted(() => {
        loadProducts();
      });

      const updateField = (field: keyof ProductRequirement, value: any) => {
        props.onChange({
          ...props.value,
          [field]: value,
        });
      };

      // Convert products to SelectableItem format
      const selectableProducts = computed((): SelectableItem[] =>
        products.value.map((product) => {
          const currency = auth.user.value?.restaurant.currency || "CHF";
          const price = parseFloat(product.price);
          return {
            id: product.id,
            name: product.name,
            subtitle: isNaN(price)
              ? undefined
              : `${currency} ${price.toFixed(2)}`,
          };
        }),
      );

      const handleSelectionChange = (selectedIds: number[]) => {
        updateField("product_ids", selectedIds);
      };

      return () => (
        <div class="card border">
          <div class="card-body p-4">
            <h4 class="card-title text-accent text-lg flex items-center gap-2">
              <div class="w-2 h-2 bg-success rounded-full"></div>
              Product Requirement
            </h4>

            <div class="space-y-4 mt-4">
              <div class="form-control">
                <label class="label">
                  <span class="label-text font-medium">Required Quantity</span>
                </label>
                <input
                  type="number"
                  value={props.value.quantity}
                  onInput={(e) =>
                    updateField(
                      "quantity",
                      parseInt((e.target as HTMLInputElement).value) || 1,
                    )
                  }
                  class="input input-bordered focus:input-primary w-full max-w-xs"
                  min="1"
                  placeholder="1"
                />
                <label class="label">
                  <span class="label-text-alt">Number of items required</span>
                </label>
              </div>

              <div class="form-control">
                <label class="label">
                  <span class="label-text font-medium">Select Products</span>
                </label>

                <SelectableListComboBox
                  items={selectableProducts.value}
                  selectedIds={props.value.product_ids || []}
                  onSelectionChange={handleSelectionChange}
                  loading={loading.value}
                  placeholder="Search products..."
                  emptyMessage="No products available"
                  selectionLabel="product(s) selected"
                  defaultShowSelected={(props.value.product_ids || []).length > 0}
                />
              </div>
            </div>
          </div>
        </div>
      );
    },
  });
